import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [token, setToken] = useState("");
  const [resending, setResending] = useState(false); const [fallbackToken, setFallbackToken] = useState("");
  const resendVerification = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.email_sent) toast.success("Verification email sent. Check your inbox."); else { setFallbackToken(data.verification_token || ""); toast.error("Couldn't send the email right now."); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResending(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/verify-email", { token }); await refresh(); toast.success("Email verified!"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-2xl space-y-6">
      <header><h1 className="font-display font-extrabold text-3xl text-slate-900">Profile</h1></header>
      <div className="rle-card p-6 space-y-2 text-sm text-slate-800">
        <Row label="Email" value={user.email}/>
        <Row label="Name" value={user.name || "—"}/>
        <Row label="Role" value={user.role}/>
        <Row label="Plan" value={user.plan}/>
        <Row label="Country" value={user.country || "—"}/>
        <Row label="English variant" value={user.english_variant || "—"}/>
        <Row label="Level" value={(user.level || "—").replace("_"," ")}/>
        <Row label="Email verified" value={user.email_verified ? "Yes" : "No"}/>
      </div>
      <Billing user={user}/>
      {!user.email_verified && (
        <div className="rle-card p-6">
          <div className="font-semibold text-slate-900">Verify your email</div>
          <p className="text-sm text-slate-600 mt-1">We sent a verification link to <b>{user.email}</b>. Didn't get it?</p>
          <Button type="button" variant="outline" disabled={resending} onClick={resendVerification} className="rounded-full mt-3" data-testid="profile-resend-verification-btn">{resending ? "Sending…" : "Resend verification email"}</Button>
          {fallbackToken && <div className="mt-3 text-xs text-slate-600">Email delivery is unavailable — use this token: <span className="font-mono break-all" data-testid="profile-fallback-token">{fallbackToken}</span></div>}
          <p className="text-sm text-slate-600 mt-4">Or paste your verification token:</p>
          <form onSubmit={verify} className="mt-3 space-y-3">
            <div><Label>Token</Label><Input value={token} onChange={(e) => setToken(e.target.value)} data-testid="profile-verify-token-input"/></div>
            <Button type="submit" className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="profile-verify-btn">Verify email</Button>
          </form>
        </div>
      )}
    </div>
  );
}
const Row = ({label, value}) => (
  <div className="flex justify-between border-b border-slate-100 py-2 last:border-0">
    <span className="text-slate-500">{label}</span><span className="font-medium capitalize">{value}</span>
  </div>
);

function Billing({ user }) {
  const [hist, setHist] = useState(null); const [opening, setOpening] = useState(false);
  useEffect(() => { api.get("/payments/history").then(({data}) => setHist(data)); }, [user.plan]);
  const exp = user.plan_expires_at ? new Date(user.plan_expires_at) : null;
  const daysLeft = exp ? Math.max(0, Math.ceil((exp - Date.now()) / 86400000)) : null;
  const planLabel = user.plan[0].toUpperCase() + user.plan.slice(1);
  const autoRenews = user.plan_source === "stripe_subscription" && ["active", "trialing"].includes(user.subscription_status) && !user.cancel_at_period_end;
  const openPortal = async () => {
    setOpening(true);
    try { const { data } = await api.post("/payments/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setOpening(false); }
  };
  return (
    <div className="rle-card p-6" data-testid="profile-billing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-slate-900 flex items-center gap-2"><CreditCard size={16}/>Plan & billing</div>
          <p className="text-sm text-slate-600 mt-1" data-testid="profile-plan-status">
            {user.role === "admin" ? "Admin — all features unlocked, no limits." :
              user.plan === "explore" ? "You're on the free Explore plan." :
              autoRenews && exp ? `${planLabel} · renews automatically on ${exp.toLocaleDateString()}.` :
              exp ? `${planLabel} access until ${exp.toLocaleDateString()} (${daysLeft} day${daysLeft === 1 ? "" : "s"} left)${user.cancel_at_period_end ? " — cancellation scheduled, won't renew." : "."}` :
              `${planLabel} plan active.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hist?.has_billing_account && user.role !== "admin" && (
            <Button variant="outline" disabled={opening} onClick={openPortal} className="rounded-full" data-testid="profile-manage-billing-btn">{opening ? "Opening…" : "Manage billing"}</Button>
          )}
          {!autoRenews && <Link to="/pricing"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="profile-upgrade-btn">{user.plan === "explore" ? "Upgrade" : "Renew or change plan"}</Button></Link>}
        </div>
      </div>
      {hist?.has_billing_account && <p className="text-xs text-slate-500 mt-3">Update your card, download invoices or cancel anytime via Manage billing. Cancellations take effect at the end of the current period.</p>}
      {hist && hist.payments.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Payment history</div>
          <ul className="text-sm space-y-1.5" data-testid="profile-payment-history">
            {hist.payments.map((p) => (
              <li key={p.session_id} className="flex justify-between gap-3">
                <span className="text-slate-700 capitalize">{p.plan} · {p.period}</span>
                <span className="text-slate-500">{new Date(p.created_at).toLocaleDateString()}</span>
                <span className={`font-medium ${p.payment_status === "paid" ? "text-emerald-700" : "text-slate-500"}`}>{p.currency.toUpperCase()} {Number(p.amount).toFixed(2)} · {p.payment_status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
